<template>
  <div>
    <v-row v-if="!arenaId">
      <v-col cols="12" class="pt-0">
        <v-alert type="info">
          <span class="mb-0 text--white">
            {{ $t('sharedAccounts.chooseArena') }}
          </span>
        </v-alert>
      </v-col>
    </v-row>

    <v-card class="elevation-2">
      <v-toolbar flat>
        <template v-if="$vuetify.breakpoint.smAndUp">
          <v-toolbar-title class="subtitle-1">
            {{ $t('navigationDrawer.configuration.sharedAccounts') }}
          </v-toolbar-title>
          <v-divider inset vertical class="mx-4" />
          <small v-if="arenaId" class="text--secondary">
            {{ pagination.total }}
          </small>
        </template>
        <v-spacer />
        <v-btn
          v-if="isAdmin || isManager"
          color="primary"
          outlined
          :icon="$vuetify.breakpoint.xsOnly"
          :disabled="!arenaId"
          class="mr-2"
          :title="$t('sharedAccounts.stats.title')"
          @click="goToStats">
          <v-icon :left="!$vuetify.breakpoint.xsOnly">mdi-chart-bar</v-icon>
          <span v-if="!$vuetify.breakpoint.xsOnly">
            {{ $t('sharedAccounts.stats.title') }}
          </span>
        </v-btn>
        <v-btn
          v-if="isAdmin"
          color="primary"
          outlined
          :icon="$vuetify.breakpoint.xsOnly"
          :disabled="!arenaId"
          class="mr-2"
          :title="$t('sharedAccounts.add.title')"
          @click="openAddDialog">
          <v-icon :left="!$vuetify.breakpoint.xsOnly">mdi-plus</v-icon>
          <span v-if="!$vuetify.breakpoint.xsOnly">
            {{ $t('sharedAccounts.add.title') }}
          </span>
        </v-btn>
        <v-btn
          color="primary"
          :icon="$vuetify.breakpoint.xsOnly"
          :outlined="$vuetify.breakpoint.xsOnly"
          :disabled="!arenaId"
          :title="$t('sharedAccounts.manualBorrow')"
          @click="manualBorrowOpen = true">
          <v-icon :left="!$vuetify.breakpoint.xsOnly">
            mdi-account-arrow-right
          </v-icon>
          <span v-if="!$vuetify.breakpoint.xsOnly">
            {{ $t('sharedAccounts.manualBorrow') }}
          </span>
        </v-btn>
      </v-toolbar>
      <v-divider />

      <!-- Filters -->
      <v-row class="px-4 pt-4 mb-2" align="center" justify="start">
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="pagination.per_page"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="!arenaId || loading"
            hide-details="auto"
            solo>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.app_id"
            :items="launcherOptions"
            :disabled="!arenaId || loading"
            :label="$t('sharedAccounts.filters.launcher')"
            clearable
            solo
            hide-details="auto" />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.sort"
            :items="sortOptions"
            :disabled="!arenaId || loading"
            :label="$t('miscellaneous.sortBy')"
            solo
            hide-details="auto" />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-text-field
            v-model="usernameSearch"
            :placeholder="$t('sharedAccounts.filters.searchUsername')"
            :disabled="!arenaId || loading"
            prepend-inner-icon="mdi-magnify"
            solo
            clearable
            hide-details />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="gameSearch"
            :placeholder="$t('sharedAccounts.filters.searchGame')"
            :disabled="!arenaId || loading"
            prepend-inner-icon="mdi-magnify"
            solo
            clearable
            hide-details />
        </v-col>
      </v-row>

      <v-divider />

      <div class="pa-4">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
          <p class="mt-3 mb-0">{{ $t('components.noData.fetchingEntries') }}</p>
        </div>

        <SharedAccountsTable
          v-else-if="arenaId && accounts.length"
          :accounts="accounts"
          :current-arena-id="arenaId"
          :session-hostnames="sessionHostnames"
          @manage="openGamesDialog"
          @history="openHistoryDialog" />

        <p v-else-if="arenaId" class="text-center py-8 mb-0">
          {{ $t('components.noData.noEntriesFound') }}
        </p>
        <p v-else class="text-center py-8 mb-0">
          {{ $t('sharedAccounts.chooseArena') }}
        </p>
      </div>

      <DataTableFooter :loading="loading" :pagination="pagination" />
    </v-card>

    <AccountGamesDialog
      v-model="gamesDialog.open"
      :account="gamesDialog.account"
      :launcher-logo="gamesDialog.launcherLogo"
      :can-manage="isAdmin"
      @edit-ban="openBanDialog"
      @edit="openEditDialog"
      @delete="confirmDelete"
      @request-ban="openBanConfirm" />

    <BorrowHistoryDialog
      v-model="historyDialog.open"
      :account="historyDialog.account"
      :launcher-logo="historyDialog.launcherLogo"
      :arena-id="arenaId" />

    <BanGameDialog
      v-model="banDialog.open"
      :account="banDialog.account"
      :game="banDialog.game"
      :loading="banDialog.saving"
      @save="saveBan" />

    <ManualBorrowDialog
      v-model="manualBorrowOpen"
      :arena-id="arenaId"
      @released="onBorrowReleased" />

    <AddAccountDialog
      v-model="addAccountOpen"
      :arena-id="arenaId"
      :launchers="launcherOptions"
      :account="editAccount"
      @created="onAccountCreated"
      @updated="onAccountUpdated" />

    <!-- Styled confirm for the destructive (soft) delete -->
    <v-dialog v-model="deleteDialog.open" max-width="400" persistent>
      <v-card v-if="deleteDialog.account">
        <v-card-title class="text-h6 d-flex align-center">
          <span>{{ $t('sharedAccounts.delete.title') }}</span>
          <v-spacer />
          <v-btn icon :disabled="deleteDialog.deleting" @click="closeDelete">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          {{
            $t('sharedAccounts.delete.confirm', {
              username: deleteDialog.account.username,
            })
          }}
          <v-alert
            v-if="deleteDialog.account.borrowed_by_session_id"
            type="warning"
            text
            dense
            class="mt-3 mb-0">
            {{ $t('sharedAccounts.delete.borrowedWarning') }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text :disabled="deleteDialog.deleting" @click="closeDelete">
            {{ $t('miscellaneous.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteDialog.deleting"
            @click="doDelete">
            {{ $t('sharedAccounts.delete.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Styled confirm for the account-level ban / remove-ban -->
    <v-dialog v-model="banConfirm.open" max-width="420" persistent>
      <v-card v-if="banConfirm.account">
        <v-card-title class="text-h6 d-flex align-center">
          <span>{{
            banConfirm.banned
              ? $t('sharedAccounts.accountBan.set')
              : $t('sharedAccounts.accountBan.remove')
          }}</span>
          <v-spacer />
          <v-btn icon :disabled="banConfirm.saving" @click="closeBanConfirm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          {{
            banConfirm.banned
              ? $t('sharedAccounts.accountBan.description')
              : $t('sharedAccounts.accountBan.removeConfirm')
          }}
          <v-alert
            v-if="
              banConfirm.banned && banConfirm.account.borrowed_by_session_id
            "
            type="warning"
            text
            dense
            class="mt-3 mb-0">
            {{ $t('sharedAccounts.accountBan.borrowedWarning') }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text :disabled="banConfirm.saving" @click="closeBanConfirm">
            {{ $t('miscellaneous.cancel') }}
          </v-btn>
          <v-btn
            :color="banConfirm.banned ? 'error' : 'primary'"
            :loading="banConfirm.saving"
            @click="doSetBan">
            {{
              banConfirm.banned
                ? $t('sharedAccounts.accountBan.set')
                : $t('sharedAccounts.accountBan.remove')
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AccountGamesDialog from './AccountGamesDialog.vue'
import AddAccountDialog from './AddAccountDialog.vue'
import AppsApi from '@/services/configuration/AppsApi'
import BanGameDialog from './BanGameDialog.vue'
import BorrowHistoryDialog from './BorrowHistoryDialog.vue'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import ManualBorrowDialog from './ManualBorrowDialog.vue'
import SessionsApi from '@/services/main/SessionsApi'
import SharedAccount from '@/models/configuration/SharedAccount'
import SharedAccountsTable from './SharedAccountsTable.vue'
import isRole from '@/mixins/crud/computed/isRole'
import { mapActions } from 'vuex'

const resetPagination = () => ({
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 0,
  from: 1,
  to: 0,
})

const resetFilters = () => ({
  app_id: null,
  sort: 'username',
})

export default {
  name: 'SharedAccounts',
  components: {
    AccountGamesDialog,
    AddAccountDialog,
    BanGameDialog,
    BorrowHistoryDialog,
    DataTableFooter,
    ManualBorrowDialog,
    SharedAccountsTable,
  },
  mixins: [isRole],
  data() {
    return {
      accounts: [],
      apps: [],
      sessionHostnames: {},
      loading: false,
      pagination: resetPagination(),
      filters: resetFilters(),
      usernameSearch: '',
      gameSearch: '',
      searchDebounce: null,
      manualBorrowOpen: false,
      addAccountOpen: false,
      editAccount: null,
      deleteDialog: {
        open: false,
        account: null,
        deleting: false,
      },
      gamesDialog: {
        open: false,
        account: null,
        launcherLogo: '',
      },
      banConfirm: {
        open: false,
        account: null,
        banned: false,
        saving: false,
      },
      historyDialog: {
        open: false,
        account: null,
        launcherLogo: '',
      },
      banDialog: {
        open: false,
        account: null,
        game: null,
        saving: false,
      },
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    appsById() {
      return this.apps.reduce((map, app) => {
        map[app.id] = { name: app.name, icon: app.icon || app.image || '' }
        return map
      }, {})
    },
    launcherOptions() {
      // /apps returns the whole app catalog; only those tagged "launcher" are
      // relevant here. Loaded accounts' launchers stay as a fallback.
      const byId = new Map()
      for (const app of this.apps) {
        const isLauncher =
          Array.isArray(app.tags) && app.tags.includes('launcher')
        if (app && app.id && isLauncher) byId.set(app.id, app.name)
      }
      for (const account of this.accounts) {
        const app = account.app
        if (app && app.id && !byId.has(app.id)) byId.set(app.id, app.name)
      }
      return [...byId.entries()]
        .map(([value, text]) => ({ text, value }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    sortOptions() {
      return [
        {
          text: this.$t('sharedAccounts.filters.usernameAsc'),
          value: 'username',
        },
        {
          text: this.$t('sharedAccounts.filters.usernameDesc'),
          value: '-username',
        },
      ]
    },
    rowsPerPageItems() {
      return [10, 25, 50, 100]
    },
  },
  watch: {
    arenaId() {
      this.pagination = resetPagination()
      this.sessionHostnames = {}
      this.fetchAccounts()
      this.fetchSessions()
    },
    'pagination.current_page'() {
      this.fetchAccounts()
    },
    'pagination.per_page'() {
      this.resetToFirstPageAndFetch()
    },
    'filters.app_id'() {
      this.resetToFirstPageAndFetch()
    },
    'filters.sort'() {
      this.resetToFirstPageAndFetch()
    },
    usernameSearch() {
      this.debouncedSearch()
    },
    gameSearch() {
      this.debouncedSearch()
    },
  },
  mounted() {
    this.fetchApps()
    if (this.arenaId) {
      this.fetchAccounts()
      this.fetchSessions()
    }
  },
  methods: {
    ...mapActions(['showSnackbar']),
    debouncedSearch() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.resetToFirstPageAndFetch()
      }, 500)
    },
    resetToFirstPageAndFetch() {
      if (this.pagination.current_page !== 1) {
        // watcher on current_page triggers the fetch
        this.pagination.current_page = 1
      } else {
        this.fetchAccounts()
      }
    },
    setParams() {
      const params = new URLSearchParams()
      params.set('page', String(this.pagination.current_page))
      params.set('size', String(this.pagination.per_page))
      params.set('sort', this.filters.sort)
      if (this.filters.app_id) params.set('app_id', this.filters.app_id)
      const username = (this.usernameSearch || '').trim()
      if (username) params.set('username', username)
      const gameName = (this.gameSearch || '').trim()
      if (gameName) params.set('game_name', gameName)
      return params.toString()
    },
    async fetchApps() {
      try {
        // /apps caps size at 100; the launcher catalog fits well within one page.
        const response = await AppsApi.get('page=1&size=100')
        this.apps = response.data?.data || []
      } catch (e) {
        console.error(e)
        this.apps = []
      }
    },
    // Resolve borrowed_by_session_id -> PC name for the current arena by
    // reusing the arena-hub sessions endpoint (same service). Shared accounts
    // are PC launchers, so we only need active "computers" sessions. Failures
    // are non-fatal: the Status column falls back to a plain "In use" badge.
    async fetchSessions() {
      if (!this.arenaId) {
        this.sessionHostnames = {}
        return
      }
      try {
        const response = await SessionsApi.getByArena(
          this.arenaId,
          'deviceId=computers&active=true&size=500',
        )
        const sessions = response.data?.data || []
        const map = {}
        for (const session of sessions) {
          const hostname = session?.device?.hostname
          if (session?.id != null && hostname) {
            map[String(session.id)] = hostname
          }
        }
        this.sessionHostnames = map
      } catch (e) {
        console.error(e)
        this.sessionHostnames = {}
      }
    },
    async fetchAccounts() {
      if (!this.arenaId) {
        this.accounts = []
        this.applyMetadata()
        return
      }
      this.loading = true
      try {
        const response = await SharedAccount.api.list(
          this.arenaId,
          this.setParams(),
        )
        const data = response.data
        const payload = Array.isArray(data) ? data : data?.data || []
        this.accounts = payload.map((dto) => new SharedAccount(dto))
        this.applyMetadata(data?.metadata)
      } catch (e) {
        console.error(e)
        this.accounts = []
        this.applyMetadata()
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.errors.loadFailed'),
        })
      } finally {
        this.loading = false
      }
    },
    applyMetadata(metadata) {
      if (!metadata) {
        this.pagination.total = 0
        this.pagination.last_page = 0
        this.pagination.from = 0
        this.pagination.to = 0
        return
      }
      const { count = 0, page = 1, size = this.pagination.per_page } = metadata
      this.pagination.total = count
      this.pagination.last_page = Math.max(1, Math.ceil(count / size))
      this.pagination.from = count === 0 ? 0 : (page - 1) * size + 1
      this.pagination.to = Math.min(page * size, count)
    },
    launcherLogoForAccount(account) {
      if (!account) return ''
      if (account.app && account.app.icon) return account.app.icon
      const fallback = this.appsById[account.app_id]
      return (fallback && fallback.icon) || ''
    },
    openGamesDialog({ account }) {
      this.gamesDialog.account = account
      this.gamesDialog.launcherLogo = this.launcherLogoForAccount(account)
      this.gamesDialog.open = true
    },
    openHistoryDialog({ account }) {
      this.historyDialog.account = account
      this.historyDialog.launcherLogo = this.launcherLogoForAccount(account)
      this.historyDialog.open = true
    },
    onBorrowReleased() {
      this.fetchAccounts()
      this.fetchSessions()
    },
    onAccountCreated() {
      this.resetToFirstPageAndFetch()
    },
    openAddDialog() {
      this.editAccount = null
      this.addAccountOpen = true
    },
    goToStats() {
      this.$router.push({
        name: 'navigationDrawer.configuration.sharedAccountsStats',
        params: { arena: this.$route.params.arena },
      })
    },
    openEditDialog({ account }) {
      this.gamesDialog.open = false
      this.editAccount = account
      this.addAccountOpen = true
    },
    onAccountUpdated() {
      // Stay on the current page; the edited row is refreshed in place.
      this.fetchAccounts()
    },
    confirmDelete({ account }) {
      this.gamesDialog.open = false
      this.deleteDialog.account = account
      this.deleteDialog.deleting = false
      this.deleteDialog.open = true
    },
    closeDelete() {
      this.deleteDialog.open = false
    },
    async doDelete() {
      const account = this.deleteDialog.account
      if (!account) return
      this.deleteDialog.deleting = true
      try {
        await SharedAccount.api.remove(this.arenaId, account.id)
        this.deleteDialog.open = false
        this.showSnackbar({
          color: 'success',
          message: this.$t('sharedAccounts.delete.success'),
        })
        // If we just removed the last row of a page beyond the first, step back.
        if (this.accounts.length === 1 && this.pagination.current_page > 1) {
          this.pagination.current_page -= 1
        } else {
          this.fetchAccounts()
        }
      } catch (e) {
        console.error(e)
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.delete.failed'),
        })
      } finally {
        this.deleteDialog.deleting = false
      }
    },
    openBanDialog({ account, game }) {
      this.banDialog.account = account
      this.banDialog.game = game
      this.banDialog.saving = false
      this.banDialog.open = true
    },
    async saveBan(bannedUntil) {
      const { account, game } = this.banDialog
      if (!account || !game) return
      this.banDialog.saving = true
      try {
        const response = await SharedAccount.api.updateGameBan(
          this.arenaId,
          account.id,
          game.game_id,
          { banned_until: bannedUntil },
        )
        // Refresh the row in place
        const newValue =
          response.data?.banned_until !== undefined
            ? response.data.banned_until
            : bannedUntil
        this.$set(game, 'banned_until', newValue)
        this.banDialog.open = false
        this.showSnackbar({
          color: 'success',
          message: bannedUntil
            ? this.$t('sharedAccounts.banDialog.banUpdated')
            : this.$t('sharedAccounts.banDialog.banRemoved'),
        })
      } catch (e) {
        console.error(e)
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.errors.updateFailed'),
        })
      } finally {
        this.banDialog.saving = false
      }
    },
    // Account-level ban (admin/manager/employee). A styled
    // confirm precedes the call, mirroring the deliberate per-game ban flow.
    openBanConfirm({ account, banned }) {
      if (!account || account.banned === banned) return
      this.banConfirm.account = account
      this.banConfirm.banned = banned
      this.banConfirm.saving = false
      this.banConfirm.open = true
    },
    closeBanConfirm() {
      this.banConfirm.open = false
    },
    async doSetBan() {
      const { account, banned } = this.banConfirm
      if (!account) return
      this.banConfirm.saving = true
      try {
        // Ban/unban goes through the update endpoint with EXACTLY { banned }.
        // Non-admins (manager/employee) may only change this single field;
        // sending anything else would be rejected with 403.
        const response = await SharedAccount.api.update(
          this.arenaId,
          account.id,
          { banned },
        )
        // The 200 returns the updated account; apply banned in place so the
        // status line and the row indicator update without a refetch.
        const newValue =
          typeof response.data?.banned === 'boolean'
            ? response.data.banned
            : banned
        this.$set(account, 'banned', newValue)
        this.banConfirm.open = false
        this.showSnackbar({
          color: 'success',
          message: newValue
            ? this.$t('sharedAccounts.accountBan.marked')
            : this.$t('sharedAccounts.accountBan.removed'),
        })
      } catch (e) {
        console.error(e)
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.accountBan.failed'),
        })
      } finally {
        this.banConfirm.saving = false
      }
    },
  },
}
</script>
